import { useLazyQuery } from "@apollo/client";
import { Building2, ChevronRight, Clock, MapPin, RefreshCw, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  useAcceptInvitation,
  useGetPendingInvitations,
} from "@/domains/onboarding/invitations/hooks";
import type { FranchiseInfo, FullSalonAccess } from "@/gql/graphql";
import { GET_USER_FRANCHISES_QUERY } from "@/graphql/auth";
import { PageContent } from "@/lib/components/page-content";
import { useAuthStore } from "@/lib/store/auth-enhanced";
import { useSalonStore } from "@/lib/store/salon";
import { SalonSelectorNavBar } from "./components/salon-selector-nav-bar";

// Show the salon search field once a user has more salons than this
const SALON_SEARCH_THRESHOLD = 8;

export function SalonSelectorPage() {
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const franchiseAccess = useSalonStore((state) => state.franchiseAccess);
  const setCurrentSalon = useSalonStore((state) => state.setCurrentSalon);
  const setFranchiseAccess = useSalonStore((state) => state.setFranchiseAccess);
  const setLoading = useSalonStore((state) => state.setLoading);

  const availableFranchises = franchiseAccess;
  const availableSalons = franchiseAccess.flatMap((fa) =>
    fa.franchise.salons.map((salon) => ({
      ...salon,
      franchise: fa.franchise,
    }))
  );
  const { logout, accessToken } = useAuthStore();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [getUserFranchisesQuery] = useLazyQuery(GET_USER_FRANCHISES_QUERY);
  const navigate = useNavigate();

  // Track if initial load has been attempted to prevent infinite re-render loops
  const hasAttemptedInitialLoad = useRef(false);

  // Fetch pending invitations
  const {
    invitations,
    loading: invitationsLoading,
    refetch: refetchInvitations,
  } = useGetPendingInvitations();
  const { acceptInvitation, loading: acceptingInvitation } = useAcceptInvitation();

  const handleSalonSelect = (salonId: string) => {
    const salon = availableSalons.find((s) => s.id === salonId);
    if (salon) {
      setCurrentSalon(salon);
      navigate("/");
    }
  };

  const handleReload = async () => {
    if (!accessToken) {
      toast.error("Authentication required", {
        description: "Please log in again to refresh data.",
      });
      return;
    }

    setIsRefreshing(true);
    setLoading(true);

    try {
      // Load franchises (includes salon data)
      const franchisesResult = await getUserFranchisesQuery({
        context: {
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
        },
        fetchPolicy: "network-only", // Force network request, skip cache
      });

      if (!franchisesResult.data?.getUserFranchises) {
        toast.error("Failed to refresh data", {
          description: "Unable to load your access information. Please try again.",
        });
        return;
      }

      // Simply use the franchise access data directly
      const franchiseAccessData = franchisesResult.data.getUserFranchises;
      setFranchiseAccess(franchiseAccessData);

      const totalSalons = franchiseAccessData.reduce(
        (total, fa) => total + fa.franchise.salons.length,
        0
      );

      if (franchiseAccessData.length === 0) {
        toast.info("No franchise access", {
          description: "You still do not have access to any franchises.",
        });
      } else if (totalSalons === 0) {
        toast.info("No salon access", {
          description: "You still do not have access to any salons within your franchises.",
        });
      } else {
        toast.success("Data refreshed", {
          description: `Found ${franchiseAccessData.length} franchise${
            franchiseAccessData.length === 1 ? "" : "s"
          } and ${totalSalons} salon${totalSalons === 1 ? "" : "s"}.`,
        });
      }
    } catch (error) {
      console.error("Failed to refresh data:", error);
      toast.error("Refresh failed", {
        description: "Unable to refresh data. Please try again or contact support.",
      });
    } finally {
      setIsRefreshing(false);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout("Logged out from salon access dialog");
  };

  const handleAcceptInvitation = async (token: string) => {
    try {
      await acceptInvitation(token);

      // Refetch both invitations and franchises
      const [, franchisesResult] = await Promise.all([
        refetchInvitations(),
        getUserFranchisesQuery({
          context: {
            headers: {
              authorization: `Bearer ${accessToken}`,
            },
          },
          fetchPolicy: "network-only",
        }),
      ]);

      if (franchisesResult.data?.getUserFranchises) {
        const franchiseAccessData = franchisesResult.data.getUserFranchises;
        setFranchiseAccess(franchiseAccessData);

        const totalSalons = franchiseAccessData.reduce(
          (total, fa) => total + fa.franchise.salons.length,
          0
        );

        // Only show success toast if there are salons
        if (totalSalons > 0) {
          toast.success("Invitation accepted", {
            description: "You now have access to the salon!",
          });
        }
      }
    } catch (error) {
      console.error("Failed to accept invitation:", error);
      toast.error("Failed to accept invitation", {
        description: "Please try again or contact support.",
      });
    }
  };

  // Auto-load franchise data on first render when no data is available
  // This ensures fresh data after accepting invitations or initial page load
  useEffect(() => {
    // Guard conditions to prevent infinite loops:
    // 1. Only run once (tracked by ref)
    // 2. Only if user is authenticated
    // 3. Only if franchise data is empty or refresh is needed
    if (
      !hasAttemptedInitialLoad.current &&
      accessToken &&
      (franchiseAccess.length === 0 || availableSalons.length === 0)
    ) {
      hasAttemptedInitialLoad.current = true;

      // Silently load franchise data on mount
      const loadInitialData = async () => {
        try {
          const franchisesResult = await getUserFranchisesQuery({
            context: {
              headers: {
                authorization: `Bearer ${accessToken}`,
              },
            },
            fetchPolicy: "network-only", // Force fresh data from server
          });

          if (franchisesResult.data?.getUserFranchises) {
            setFranchiseAccess(franchisesResult.data.getUserFranchises);
          }
        } catch (error) {
          console.error("Failed to load initial franchise data:", error);
        }
      };

      loadInitialData();
    }
  }, [
    accessToken,
    franchiseAccess.length,
    availableSalons.length,
    getUserFranchisesQuery,
    setFranchiseAccess,
  ]);

  // Show the search field once there are enough salons to make filtering useful
  const showSearch = availableSalons.length > SALON_SEARCH_THRESHOLD;

  // Filter salons based on search query
  const filteredSalons = useMemo(() => {
    if (!searchQuery.trim()) {
      return availableSalons;
    }

    const query = searchQuery.toLowerCase();
    return availableSalons.filter(
      (salon) =>
        salon.name.toLowerCase().includes(query) ||
        salon.city?.toLowerCase().includes(query) ||
        salon.state?.toLowerCase().includes(query) ||
        salon.franchise?.name?.toLowerCase().includes(query)
    );
  }, [availableSalons, searchQuery]);

  const hasNoAccess = availableFranchises.length === 0 || availableSalons.length === 0;

  // Group salons by franchise and separate single vs multi-salon franchises
  const groupedSalons = filteredSalons.reduce(
    (acc, salon) => {
      const franchiseId = salon.franchise?.id || "unknown";

      if (!acc[franchiseId]) {
        acc[franchiseId] = {
          franchise: salon.franchise as FranchiseInfo,
          salons: [],
        };
      }
      acc[franchiseId].salons.push(salon as FullSalonAccess & { franchise: FranchiseInfo });
      return acc;
    },
    {} as Record<
      string,
      {
        franchise: FranchiseInfo | null | undefined;
        salons: (FullSalonAccess & { franchise: FranchiseInfo })[];
      }
    >
  );

  // Separate single-salon franchises from multi-salon franchises
  const singleSalonFranchises: (FullSalonAccess & {
    franchise: FranchiseInfo;
  })[] = [];
  const multiSalonFranchises: Record<
    string,
    {
      franchise: FranchiseInfo | null | undefined;
      salons: (FullSalonAccess & { franchise: FranchiseInfo })[];
    }
  > = {};

  Object.entries(groupedSalons).forEach(([franchiseId, { franchise, salons }]) => {
    if (salons.length === 1) {
      // Single salon franchise - add to the combined list
      singleSalonFranchises.push(salons[0]);
    } else {
      // Multi salon franchise - keep in grouped structure
      multiSalonFranchises[franchiseId] = { franchise, salons };
    }
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Navigation Bar */}
      <SalonSelectorNavBar
        onReload={handleReload}
        onLogout={handleLogout}
        isRefreshing={isRefreshing}
        showSearch={showSearch}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        filteredSalonsCount={filteredSalons.length}
      />

      {/* Content */}
      <PageContent className="mt-0">
        <div className="mx-auto max-w-4xl px-3 sm:px-4 py-4 sm:pt-0 sm:pb-6">
          {/* Pending Invitations Section - Only show if there are invitations */}
          {invitations.length > 0 && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-3 ml-4">
                Pending Invitations
              </h2>
              {invitationsLoading ? (
                <Card className="bg-white">
                  <CardContent className="p-8 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-3 text-slate-400" />
                    <p className="text-sm text-slate-600">Loading invitations...</p>
                  </CardContent>
                </Card>
              ) : (
                <Card className="overflow-hidden shadow-sm bg-white">
                  <CardContent className="p-0">
                    <div className="divide-y divide-slate-100">
                      {invitations.map((invitation) => (
                        <div
                          key={invitation.id}
                          className="p-4 hover:bg-slate-50 transition-colors duration-200"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-base font-semibold text-slate-900 mb-1">
                                {invitation.salonName}
                              </h3>
                              {invitation.workerName && (
                                <p className="text-sm text-slate-600 mb-2">
                                  Role: {invitation.workerName}
                                </p>
                              )}
                              {invitation.message && (
                                <p className="text-sm text-slate-600 mb-2 italic">
                                  "{invitation.message}"
                                </p>
                              )}
                              <div className="flex items-center gap-2 text-xs text-slate-500">
                                <Clock className="w-3 h-3" />
                                <span>
                                  Expires:{" "}
                                  {invitation.expiresAt
                                    ? new Date(invitation.expiresAt).toLocaleDateString()
                                    : "N/A"}
                                </span>
                              </div>
                            </div>
                            <Button
                              onClick={() => handleAcceptInvitation(invitation.token)}
                              disabled={acceptingInvitation}
                              size="sm"
                              className="flex-shrink-0"
                            >
                              {acceptingInvitation ? (
                                <>
                                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                  Accepting...
                                </>
                              ) : (
                                "Accept"
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* No Access Empty State */}
          {hasNoAccess ? (
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-3 ml-4">Your Salons</h2>
              <Card className="bg-white">
                <CardContent className="p-8 text-center">
                  <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                  <p className="text-sm text-slate-600 mb-4">
                    You don't have access to any{" "}
                    {availableFranchises.length === 0 ? "franchises" : "salons"} yet.
                  </p>
                  <div className="flex gap-2 justify-center">
                    <Button
                      onClick={handleReload}
                      variant="default"
                      size="sm"
                      disabled={isRefreshing}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`} />
                      {isRefreshing ? "Refreshing..." : "Refresh"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <>
              {/* Franchises and Salons List */}
              {singleSalonFranchises.length === 0 &&
              Object.keys(multiSalonFranchises).length === 0 &&
              searchQuery ? (
                <div className="text-center py-12">
                  <div className="mx-auto w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                    <Search className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No salons found</h3>
                  <p className="text-slate-600 max-w-md mx-auto">
                    No salons match your search criteria. Try adjusting your search terms.
                  </p>
                </div>
              ) : (
                <div className="space-y-6 sm:space-y-8">
                  {/* Single Salon Franchises - grouped without headers */}
                  {singleSalonFranchises.length > 0 && (
                    <Card className="overflow-hidden shadow-sm bg-white">
                      <CardContent className="p-0">
                        <div className="divide-y divide-slate-100">
                          {singleSalonFranchises.map((salon) => (
                            <div
                              key={salon.id}
                              data-testid={`salon-card-${salon.id}`}
                              className={`group cursor-pointer hover:bg-slate-50 transition-colors duration-200 p-3 sm:p-4 lg:py-1 touch-manipulation min-h-16 flex items-center ${
                                currentSalon?.id === salon.id
                                  ? "bg-blue-50 border-l-4 border-blue-500"
                                  : ""
                              }`}
                              onClick={() => handleSalonSelect(salon.id)}
                            >
                              <div className="flex items-center justify-between h-full w-full">
                                <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                                  {/* Salon Info */}
                                  <div className="flex-1 min-w-0">
                                    <h3
                                      className={`text-sm sm:text-base font-semibold group-hover:text-blue-600 transition-colors duration-200 truncate ${
                                        currentSalon?.id === salon.id
                                          ? "text-blue-900"
                                          : "text-slate-900"
                                      }`}
                                    >
                                      {salon.name}
                                      {currentSalon?.id === salon.id && (
                                        <span className="ml-2 text-xs font-medium text-blue-600">
                                          (Current)
                                        </span>
                                      )}
                                    </h3>
                                    {salon.city && (
                                      <div className="flex items-center gap-2 mt-0.5 flex-wrap sm:flex-nowrap">
                                        <div className="flex items-center gap-1 text-xs sm:text-sm text-slate-500">
                                          <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
                                          <span className="truncate">{salon.city}</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Arrow */}
                                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 group-hover:text-blue-500 transition-colors duration-200 flex-shrink-0" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Multi-Salon Franchises - with headers */}
                  {Object.entries(multiSalonFranchises).map(
                    ([franchiseId, { franchise, salons }]) => {
                      return (
                        <div key={franchiseId} className="space-y-2">
                          {/* Franchise Header */}
                          <div>
                            <h2 className="text-lg sm:text-xl font-semibold text-slate-900 truncate ml-4">
                              {franchise?.name || "Independent Salon"}
                            </h2>
                          </div>

                          {/* Salon List */}
                          <Card className="overflow-hidden shadow-sm bg-white">
                            <CardContent className="p-0">
                              <div className="divide-y divide-slate-100">
                                {salons.map((salon) => (
                                  <div
                                    key={salon.id}
                                    data-testid={`salon-card-${salon.id}`}
                                    className={`group cursor-pointer hover:bg-slate-50 transition-colors duration-200 p-3 sm:p-4 lg:py-1 touch-manipulation min-h-16 flex items-center ${
                                      currentSalon?.id === salon.id
                                        ? "bg-blue-50 border-l-4 border-blue-500"
                                        : ""
                                    }`}
                                    onClick={() => handleSalonSelect(salon.id)}
                                  >
                                    <div className="flex items-center justify-between h-full w-full">
                                      <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                                        {/* Salon Info */}
                                        <div className="flex-1 min-w-0">
                                          <h3
                                            className={`text-sm sm:text-base font-semibold group-hover:text-blue-600 transition-colors duration-200 truncate ${
                                              currentSalon?.id === salon.id
                                                ? "text-blue-900"
                                                : "text-slate-900"
                                            }`}
                                          >
                                            {salon.name}
                                            {currentSalon?.id === salon.id && (
                                              <span className="ml-2 text-xs font-medium text-blue-600">
                                                (Current)
                                              </span>
                                            )}
                                          </h3>
                                          {salon.city && (
                                            <div className="flex items-center gap-2 mt-0.5 flex-wrap sm:flex-nowrap">
                                              <div className="flex items-center gap-1 text-xs sm:text-sm text-slate-500">
                                                <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
                                                <span className="truncate">{salon.city}</span>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      {/* Arrow */}
                                      <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 group-hover:text-blue-500 transition-colors duration-200 flex-shrink-0" />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      );
                    }
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </PageContent>
    </div>
  );
}
